/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  configureStore,
  combineReducers,
  Reducer,
  Action,
  AnyAction,
  ReducersMapObject,
  createSlice,
  EnhancedStore,
  Dispatch,
  MiddlewareAPI,
  StoreEnhancer,
  SerializableStateInvariantMiddlewareOptions,
  ImmutableStateInvariantMiddlewareOptions,
  ThunkMiddleware,
} from '@reduxjs/toolkit';
import { ReactNode } from 'react';
import { EqualityFn, Provider, useSelector } from 'react-redux';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const injectedReducers: ReducersMapObject<any, any> = {};

const store = configureStore({
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }),
  devTools: false,
  reducer: combineReducers({
    defaultSlice: createSlice({
      name: 'defaultSlice',
      initialState: {},
      reducers: {
        update() {},
      },
    }).reducer,
  }),
});

type TypedUseSelectorHook<TState> = <TSelected>(
  selector: (state: TState) => TSelected,
  equalityFn?: EqualityFn<TSelected>,
) => TSelected;

/**
 * Esta función permite agregar reducers al store en forma dinámica.
 *
 * @example
 *
    import { injectReducers, createSlice } from '@apia/store';

    const slice = createSlice({
      name: 'holaMundo',
      initialState: {
        hola: 'mundo',
      } as { hola: string },
      reducers: {
        updateMundo(state) {
          state.hola = 'world';
        },
      },
    });

    const store = injectReducers({
      holaMundo: slice.reducer,
    });

    store.dispatch(slice.actions.updateMundo());

    // Ahora el store dice hola: 'world'
 */
type Enhancers = readonly StoreEnhancer[];
interface ThunkOptions<E = any> {
  extraArgument: E;
}
interface GetDefaultMiddlewareOptions {
  thunk?: boolean | ThunkOptions;
  immutableCheck?: boolean | ImmutableStateInvariantMiddlewareOptions;
  serializableCheck?: boolean | SerializableStateInvariantMiddlewareOptions;
}

type ThunkMiddlewareFor<
  S,
  O extends GetDefaultMiddlewareOptions = {},
> = O extends {
  thunk: false;
}
  ? never
  : O extends {
        thunk: {
          extraArgument: infer E;
        };
      }
    ? ThunkMiddleware<S, AnyAction, E>
    : ThunkMiddleware<S>;

export type Middleware<S = any, D extends Dispatch = Dispatch> = (
  api: MiddlewareAPI<D, S>,
) => (next: Dispatch) => (action: any) => any;
type Middlewares<S> = readonly Middleware<S>[];

export function injectReducers<
  S,
  A extends Action = AnyAction,
  M extends Middlewares<S> = [ThunkMiddlewareFor<S>],
  E extends Enhancers = [StoreEnhancer],
>(reducers: ReducersMapObject<S, A>) {
  Object.entries(reducers).forEach(([name, value]) => {
    injectedReducers[name] = value as Reducer;
  });

  const newReducer = combineReducers(injectedReducers);
  store.replaceReducer(newReducer);
  const newUseSelector: TypedUseSelectorHook<S> = useSelector;

  return {
    store: store as EnhancedStore<S, A, M, E>,
    useSelector: newUseSelector,
  };
}

export const StoreProvider = ({ children }: { children: ReactNode }) => {
  return <Provider store={store}>{children}</Provider>;
};
